<template>
  <div class="w-full relative">

    <!-- Hero variant toggle: add more entries to heroVariants in script to support more versions -->
    <!-- <div class="fixed top-4 right-4 z-50 flex gap-1 p-1 rounded-lg bg-base-200/90 backdrop-blur-sm border border-base-300 shadow-sm" role="group" aria-label="Hero variant selector">
      <button
        v-for="(label, idx) in heroVariants"
        :key="idx"
        type="button"
        :class="[
          'px-3 py-1.5 text-sm font-medium rounded transition-colors',
          activeHeroVariant === idx
            ? 'bg-primary text-primary-content'
            : 'text-secondary hover:bg-base-300'
        ]"
        :aria-pressed="activeHeroVariant === idx"
        :aria-label="`Show hero ${label}`"
        @click="activeHeroVariant = idx"
      >
        {{ label }}
      </button>
    </div> -->

    <div class="w-full prose max-w-7xl mx-auto">

      <!-- Hero A: AI Superpowers -->
      <div v-show="activeHeroVariant === 0" class="flex flex-col md:flex-row" style="min-height:80vh;">

        <div class="w-full flex flex-col justify-center m-auto p-3">

          <p class="text-sm text-secondary">
            Product Management has evolved
          </p>
          <h1 class="mb-4 text-4xl md:text-5xl font-bold leading-tight">
            <span class="gradient-text-1">AI Superpowers</span> for Product Managers
          </h1>

          <p class="text-xl text-secondary my-2 leading-relaxed">
            Engineers have Claude, CoPilot, Cursor...
            <br>
            Product Managers have SupaPM.
          </p>
          
          <p class="text-xl text-secondary my-2 leading-relaxed">
            Local-first • Easy setup • <span class="gradient-text-1 font-bold">Free</span>
          </p>

          <!-- Update Chrome URL to direct link when published: chromewebstore.google.com/detail/supapm/[extension-id] -->
          <div class="flex flex-col sm:flex-row gap-4 mt-6 mb-3">
            <a href="https://chromewebstore.google.com/detail/supapm/pjopbojlonaimbfcomcnbadlagcldbgp" target="_blank"
              rel="noopener noreferrer"
              class="btn btn-soft px-8 py-2 font-semibold inline-flex items-center justify-center gap-2">
              <img src="@/assets/icons/browser-icons/chrome.svg" class="w-7 h-7 m-0" alt="" aria-hidden="true" />
              Chrome Extension
            </a>
            <a href="https://addons.mozilla.org/en-US/firefox/addon/supapm/" target="_blank" rel="noopener noreferrer"
              class="btn btn-soft px-8 py-2 inline-flex items-center justify-center gap-2">
              <img src="@/assets/icons/browser-icons/firefox.svg" class="w-7 h-7 m-0" alt="" aria-hidden="true" />
              Firefox Add-on
            </a>
          </div>

          <!-- ASPIRATIONAL - for future reference
          <p class="text-xl text-secondary my-2 leading-relaxed">
            Supercharge your product team with AI agents and automations that work round-the-clock to improve your product.
          </p>
          <p class="text-xl text-gray-600 my-2 leading-relaxed">
            Stop losing customer insights in Slack threads and meeting notes. SupaPM automatically captures, analyzes, and prioritizes every piece of feedback so you build what customers actually want.
          </p>
          Key Benefits (aspirational):
          - Auto-capture insights: Our AI agents extract insights from your customer conversations
          - AI backlog prioritisation: Instantly filter out noise and focus on what's important
          - Build with confidence: Launch features your customers actually want
          CTA: Join Waitlist | Calculate My ROI
          -->

        </div>


      </div>

    </div>

    <!-- Benefits Grid -->
    <div class="py-16 bg-base">
      <div class="max-w-6xl mx-auto px-6">
        <div class="text-center mb-10">
          <h2 class="text-3xl md:text-4xl font-bold text-primary mb-2">Built for modern product teams</h2>
          <p class="text-secondary">Everything you need, exactly where you already work.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose">

          <article class="p-7 rounded-2xl border border-base-300 bg-base-100 shadow-sm benefits-card">
            <p class="text-xs uppercase tracking-wider text-secondary mb-2">Local-first</p>
            <h3 class="text-2xl font-bold text-primary mb-3">Sensitive company data stays within approved walls</h3>
            <p class="text-secondary leading-relaxed">
              SupaPM keeps your data in your browser. Sensitive info never leaves your device — only your own LLM calls (BYOK) go external.
            </p>
          </article>


          <article class="p-7 rounded-2xl border border-base-300 bg-base-100 shadow-sm benefits-card">
            <p class="text-xs uppercase tracking-wider text-secondary mb-2">Context</p>
            <h3 class="text-2xl font-bold text-primary mb-3">Context where you need it - right in the browser</h3>
            <p class="text-secondary leading-relaxed">
              Context switching is easy now? With SupaPM, your project details, decisions, and customer signals are literally at your side when you need them.
            </p>
          </article>

          <article class="p-7 rounded-2xl border border-base-300 bg-base-100 shadow-sm benefits-card">
            <p class="text-xs uppercase tracking-wider text-secondary mb-2">Skills</p>
            <h3 class="text-2xl font-bold text-primary mb-3">An LLM that does things how you need them done</h3>
            <p class="text-secondary leading-relaxed">
              Quickly build skills that suit your workflow. Skills are automatically used when needed. 
            </p>
          </article>

          <article class="p-7 rounded-2xl border border-base-300 bg-base-100 shadow-sm benefits-card">
            <p class="text-xs uppercase tracking-wider text-secondary mb-2">Fully configurable</p>
            <h3 class="text-2xl font-bold text-primary mb-3">Custom Commands and System Prompts</h3>
            <p class="text-secondary leading-relaxed">
              You'll start with sane defaults, but you can easily update your system prompt and custom commands to suit your daily work.
            </p>
          </article>
        </div>
      </div>
    </div>

    <!-- Security Section -->
    <section class="py-16 bg-base">
      <div class="max-w-6xl mx-auto px-6">
        <div class="card bg-base-200 shadow-lg">
          <div class="card-body p-8 md:p-10">
            <div class="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div>
                <p class="text-xs uppercase tracking-wider text-secondary mb-3">Security and transparency</p>
                <h2 class="text-3xl md:text-4xl font-bold text-primary mb-4">
                  Built for product teams that need trust before adoption
                </h2>
                <p class="text-secondary leading-relaxed mb-6">
                  SupaPM's extension is designed as a context layer, not a browser automation agent.
                  It reads page context to help you work, but it does not click buttons, submit forms,
                  or make destructive changes inside the websites you visit.
                </p>
                <NuxtLink to="/security" class="btn btn-primary">
                  Review our security posture
                </NuxtLink>
              </div>

              <div class="grid gap-3 not-prose">
                <div class="rounded-xl bg-base-100 p-4">
                  <h3 class="font-semibold text-primary mb-1">Read-only page context</h3>
                  <p class="text-sm text-secondary">
                    Uses URL, title, selected text, and page context to answer with the right project context.
                  </p>
                </div>
                <div class="rounded-xl bg-base-100 p-4">
                  <h3 class="font-semibold text-primary mb-1">Local-first by default</h3>
                  <p class="text-sm text-secondary">
                    Projects, chats, skills, prompts, and settings live in the browser unless you choose otherwise.
                  </p>
                </div>
                <div class="rounded-xl bg-base-100 p-4">
                  <h3 class="font-semibold text-primary mb-1">Public extension SBOM</h3>
                  <p class="text-sm text-secondary">
                    Security teams can inspect the extension dependency supply chain in CycloneDX format.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Updates Section -->
    <div id="updates" class="py-20 bg-surface">
      <div class="prose max-w-4xl mx-auto px-6">
        <h2 class="text-3xl font-bold mb-2 text-primary">Latest updates</h2>
        <p class="text-secondary mb-8">
          Stay up to date with SupaPM developments
        </p>

        <div
          v-for="(update, index) in latestHomepageUpdates"
          :key="update.slug"
          class="not-prose card bg-base-100 border border-base-300 shadow-sm overflow-hidden mb-6"
        >
          <div class="card-body p-0">
            <div class="grid md:grid-cols-2 gap-0">
              <div v-if="update.videoId" class="aspect-video md:aspect-auto md:min-h-[200px] bg-base-200">
                <iframe
                  :src="`https://www.youtube.com/embed/${update.videoId}`"
                  :title="`${update.version} SupaPM update`"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                  class="w-full h-full min-h-[200px]"
                />
              </div>
              <div v-else class="min-h-[200px] bg-base-200 p-6 flex flex-col justify-center">
                <p v-if="index === 0" class="text-xs uppercase tracking-wider text-secondary mb-3">Latest release</p>
                <p class="text-2xl font-bold text-primary m-0">{{ update.version }}</p>
              </div>
              <div class="p-6 flex flex-col justify-center">
                <div class="mb-2 flex flex-wrap items-center gap-2">
                  <span class="badge badge-primary badge-sm w-fit">{{ update.version }}</span>
                  <span class="text-xs font-medium text-secondary">{{ update.date }}</span>
                </div>
                <h3 class="text-xl font-bold text-primary mb-2">{{ update.title }}</h3>
                <p class="text-secondary text-sm mb-4">
                  {{ update.description }}
                </p>
                <NuxtLink :to="`/updates/${update.slug}`" class="btn btn-outline btn-sm w-fit">
                  Read more
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>

        <p class="mt-4 text-secondary text-sm">
          <NuxtLink to="/updates" class="link link-primary">View all updates →</NuxtLink>
        </p>

        <div class="mt-10">
          <UpdateSubscribeForm />
        </div>
      </div>
    </div>

    <!-- ASPIRATIONAL - Social Proof / Problem Statement (for future reference)
    <div class="py-20 bg-surface">
      <div class="prose max-w-4xl mx-auto px-6 text-center">
        <h2 class="text-3xl font-bold mb-8 text-primary">The Problem Every Product Team Faces</h2>

        <div class="grid md:grid-cols-3 gap-8 not-prose">
          <div class="p-6">
            <div class="text-red-500 text-4xl mb-4">😤</div>
            <h4 class="font-bold text-lg mb-2 text-primary">Insights Get Lost</h4>
            <p class="text-secondary">Critical customer feedback disappears in Slack threads, email chains, and meeting
              notes.</p>
          </div>

          <div class="p-6">
            <div class="text-red-500 text-4xl mb-4">🎯</div>
            <h4 class="font-bold text-lg mb-2 text-primary">Wrong Priorities</h4>
            <p class="text-secondary">Without systematic insight tracking, teams build features customers don't actually
              want.</p>
          </div>

          <div class="p-6">
            <div class="text-red-500 text-4xl mb-4">⏰</div>
            <h4 class="font-bold text-lg mb-2 text-primary">Time Wasted</h4>
            <p class="text-secondary">Hours spent manually synthesizing feedback instead of building great products.</p>
          </div>
        </div>

        <div class="mt-12">
          <p class="text-xl text-primary mb-8">
            <strong>SupaPM is your AI copilot for product teams.</strong> Automatically capture, analyze, and prioritize
            every piece of customer feedback.
          </p>

          <NuxtLink class="btn btn-primary px-8 py-3" to="/login">
            Join Waitlist
          </NuxtLink>
        </div>
      </div>
    </div>
    -->

    <!-- ROI Calculator Section -->
    <div id="roi-calculator" class="bg-base py-20 hidden">
      <div class="max-w-6xl mx-auto px-6">

        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold mb-4 text-primary">Stop losing insights. Start winning customers.</h2>
          <p class="text-xl text-secondary">Your team is sitting on a goldmine of customer feedback. SupaPM helps you
            mine it.</p>
        </div>

        <!-- Interactive ROI Calculator -->
        <div class="mb-16">
          <RoiCalculator />
        </div>

      </div>
    </div>


    <!-- How It Works -->
    <div class="py-20 bg-base hidden" id="realtime-feedback-analysis">
      <div class="prose max-w-6xl mx-auto px-6">

        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold mb-4 text-primary">AI Native Product Management in 3 Steps</h2>
          <p class="text-xl text-secondary">Transform scattered feedback into strategic roadmap with intelligent
            automation</p>
        </div>

        <div class="grid md:grid-cols-3 gap-8 not-prose">

          <div class="text-center">
            <div class="bg-blue-100 dark:bg-blue-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <span class="text-blue-600 dark:text-blue-300 font-bold text-xl">1</span>
            </div>
            <h3 class="text-xl font-bold mb-4 text-primary">Connect Your Tools</h3>
            <p class="text-secondary">
              Link your CRM (HubSpot, Salesforce), support tools (Intercom, Zendesk), and call recordings (Gong,
              Chorus).
            </p>
          </div>

          <div class="text-center">
            <div class="bg-green-100 dark:bg-green-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <span class="text-green-600 dark:text-green-300 font-bold text-xl">2</span>
            </div>
            <h3 class="text-xl font-bold mb-4 text-primary">AI Extracts Insights</h3>
            <p class="text-secondary">
              Our AI agents automatically identify customer pain points, feature requests, and competitive intelligence
              from every interaction.
            </p>
          </div>

          <div class="text-center">
            <div class="bg-purple-100 dark:bg-purple-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <span class="text-purple-600 dark:text-purple-300 font-bold text-xl">3</span>
            </div>
            <h3 class="text-xl font-bold mb-4 text-primary">Prioritize & Build</h3>
            <p class="text-secondary">
              Get data-driven roadmap recommendations with impact scores, so you build what customers actually want.
            </p>
          </div>

        </div>

      </div>
    </div>

    <!-- Competitor Monitoring Section -->
    <div class="py-20 bg-surface hidden" id="competitor-monitoring">
      <div class="prose max-w-4xl mx-auto px-6">

        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold mb-4 text-primary">Stay Ahead of Competition</h2>
          <p class="text-xl text-secondary">
            While you focus on customers, our AI monitors your competitive landscape 24/7.
          </p>
        </div>

        <div class="grid md:grid-cols-2 gap-12 not-prose">

          <div>
            <h3 class="text-xl font-bold mb-4 text-primary">Automatic Competitor Tracking</h3>
            <ul class="space-y-3 text-secondary">
              <li class="flex items-start">
                <img src="@/assets/icons/checkmark.svg" class="w-5 h-5 mr-3 mt-1" alt="checkmark" />
                Monitor competitor product updates and releases
              </li>
              <li class="flex items-start">
                <img src="@/assets/icons/checkmark.svg" class="w-5 h-5 mr-3 mt-1" alt="checkmark" />
                Track pricing changes and new feature announcements
              </li>
              <li class="flex items-start">
                <img src="@/assets/icons/checkmark.svg" class="w-5 h-5 mr-3 mt-1" alt="checkmark" />
                Analyze competitor strengths and weaknesses
              </li>
              <li class="flex items-start">
                <img src="@/assets/icons/checkmark.svg" class="w-5 h-5 mr-3 mt-1" alt="checkmark" />
                Get alerts when you need to respond
              </li>
            </ul>
          </div>

          <div>
            <h3 class="text-xl font-bold mb-4 text-primary">Strategic Intelligence</h3>
            <p class="text-secondary mb-4">
              Don't just track what competitors are doing—understand why they're doing it and how it affects your
              strategy.
            </p>
            <p class="text-primary font-semibold">
              Turn competitive intelligence into competitive advantage.
            </p>
          </div>

        </div>

      </div>
    </div>

    <!-- Pricing Section -->
    <div class="py-20 bg-base hidden">
      <div class="max-w-6xl mx-auto px-6">
        
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold mb-4 text-primary">Simple, Transparent Pricing</h2>
          <p class="text-xl text-secondary">Choose the plan that fits your team's needs</p>
        </div>

        <div class="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          
          <!-- Starter Plan -->
          <div class="bg-surface p-8 rounded-lg shadow-sm border-2 border-border relative">
            <div class="text-center mb-6">
              <h3 class="text-2xl font-bold text-primary mb-2">Starter</h3>
              <p class="text-secondary mb-4">Perfect for growing product teams</p>
              <div class="mb-6">
                <span class="text-4xl font-bold text-primary">$29</span>
                <span class="text-secondary">/month per seat</span>
              </div>
            </div>

            <ul class="space-y-3 mb-8">
              <li class="flex items-center">
                <img src="@/assets/icons/tick-circle.svg" class="w-5 h-5 mr-3" alt="checkmark" />
                <span class="text-primary">Unlimited insight capture</span>
              </li>
              <li class="flex items-center">
                <img src="@/assets/icons/tick-circle.svg" class="w-5 h-5 mr-3" alt="checkmark" />
                <span class="text-primary">AI-powered prioritization</span>
              </li>
              <li class="flex items-center">
                <img src="@/assets/icons/tick-circle.svg" class="w-5 h-5 mr-3" alt="checkmark" />
                <span class="text-primary">Basic integrations (HubSpot, Slack)</span>
              </li>
              <li class="flex items-center">
                <img src="@/assets/icons/tick-circle.svg" class="w-5 h-5 mr-3" alt="checkmark" />
                <span class="text-primary">Email support</span>
              </li>
              <li class="flex items-center">
                <img src="@/assets/icons/tick-circle.svg" class="w-5 h-5 mr-3" alt="checkmark" />
                <span class="text-primary">Up to 10 team members</span>
              </li>
            </ul>

            <NuxtLink class="btn btn-primary w-full py-3 text-center font-semibold" to="/login">
              Join Waitlist
            </NuxtLink>
          </div>

          <!-- Enterprise Plan -->
          <div class="bg-surface p-8 rounded-lg shadow-sm border-2 border-blue-500 relative">
            <div class="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span class="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">Popular</span>
            </div>
            
            <div class="text-center mb-6">
              <h3 class="text-2xl font-bold text-primary mb-2">Enterprise</h3>
              <p class="text-secondary mb-4">For teams that need advanced features</p>
              <div class="mb-6">
                <span class="text-4xl font-bold text-primary">Custom</span>
              </div>
            </div>

            <ul class="space-y-3 mb-8">
              <li class="flex items-center">
                <img src="@/assets/icons/tick-circle.svg" class="w-5 h-5 mr-3" alt="checkmark" />
                <span class="text-primary">Everything in Starter</span>
              </li>
              <li class="flex items-center">
                <img src="@/assets/icons/tick-circle.svg" class="w-5 h-5 mr-3" alt="checkmark" />
                <span class="text-primary">Advanced integrations (Salesforce, Gong)</span>
              </li>
              <li class="flex items-center">
                <img src="@/assets/icons/tick-circle.svg" class="w-5 h-5 mr-3" alt="checkmark" />
                <span class="text-primary">Custom AI training</span>
              </li>
              <li class="flex items-center">
                <img src="@/assets/icons/tick-circle.svg" class="w-5 h-5 mr-3" alt="checkmark" />
                <span class="text-primary">Priority support & onboarding</span>
              </li>
              <li class="flex items-center">
                <img src="@/assets/icons/tick-circle.svg" class="w-5 h-5 mr-3" alt="checkmark" />
                <span class="text-primary">Unlimited team members</span>
              </li>
              <li class="flex items-center">
                <img src="@/assets/icons/tick-circle.svg" class="w-5 h-5 mr-3" alt="checkmark" />
                <span class="text-primary">SSO & advanced security</span>
              </li>
            </ul>

            <a href="mailto:hello@supapm.com" class="btn btn-outline w-full py-3 text-center font-semibold">
              Contact Sales
            </a>
          </div>

        </div>

        <div class="text-center mt-12">
          <p class="text-secondary mb-4">
            🔒 14-day free trial • No credit card required • Cancel anytime
          </p>
          <p class="text-sm text-secondary">
            Questions about pricing? <a href="mailto:hello@supapm.com" class="text-blue-600 hover:underline">Get in touch</a>
          </p>
        </div>

      </div>
    </div>

        <!-- CTA Section -->
    <div class="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white dark:from-blue-700 dark:to-purple-700 hidden" id="product-research">
      <div class="prose max-w-4xl mx-auto px-6 text-center not-prose">
        
        <h2 class="text-4xl font-bold mb-6 text-white">Ready for the new era of product management?</h2>
        
        <p class="text-xl mb-8 text-blue-100 dark:text-blue-200">
          Join product teams who've transformed scattered feedback into strategic advantage.
        </p>

        <div class="mb-8">
          <div class="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <div class="text-3xl font-bold text-white">Hours</div>
              <div class="text-blue-200 dark:text-blue-300">saved every week</div>
            </div>
            <div>
              <div class="text-3xl font-bold text-white">100%</div>
              <div class="text-blue-200 dark:text-blue-300">insight coverage</div>
            </div>
            <div>
              <div class="text-3xl font-bold text-white">Zero</div>
              <div class="text-blue-200 dark:text-blue-300">admin overhead</div>
            </div>
          </div>
        </div>

        <NuxtLink class="btn bg-white text-blue-600 hover:bg-gray-100 dark:bg-gray-100 dark:text-blue-700 dark:hover:bg-gray-200 px-8 py-4 font-semibold rounded-lg inline-block"
          to="/login">
          Join Waitlist
        </NuxtLink>
        
        <p class="text-sm text-blue-200 dark:text-blue-300 mt-4">
          No credit card required • Setup in under 5 minutes
        </p>

      </div>
    </div>

    <!-- 
    <div class="prose mt-20 max-w-4xl mx-auto">

      <h2>
        Our Ethos
      </h2>

      <p>
        AI is giving Product leaders superpowers. Your team can now have an army of assistants working round-the-clock to process feedback, research competitors, monitor the market and help them make the right decisions at the right time.
      </p>

    </div>


    <div class="prose mt-20 max-w-4xl mx-auto" id="realtime-feedback-analysis">

      <h2>
        Real-time Feedback Analysis
      </h2>

      <p>
        Your CRM, support tickets, social media and more are all sources of feedback. Our AI can process this feedback in real-time, giving you insights into what your customers are saying about your product, what they like, what they don't like and what they want to see next.
      </p>

      <p class="font-bold">
        Know your priorities in real-time
      </p>

      
    </div>

    <div class="prose mt-20 max-w-4xl mx-auto" id="competitor-monitoring">

      <h2>
        Competitor Monitoring
      </h2>

      <p>
        Your competitors are always up to something. Our AI can monitor the market and your competitors, giving you immediate alerts when you should know something. 
      </p>

      <p class="font-bold">
        Stay ahead of the game
      </p>

    </div>

    <div class="prose mt-20 max-w-4xl mx-auto" id="product-research">
        
        <h2>
          Product Research
        </h2>
  
        <p>
          Research features, plan launch campaigns and hit your product goals with our AI product launcher. 
        </p>
  
        <p class="font-bold">
          Launch your product with confidence
        </p>

    </div> -->

  </div>
</template>

<style scoped lang="scss">

.features-list {
  list-style-type: none;
  padding-left: 0;

  li {
    padding-left: 0px;
  }
}

.homepage-abstract-background {
  position: relative;
  overflow: hidden;
}

.benefits-card {
  position: relative;
  overflow: hidden;
}

.benefits-card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(circle at 80% 10%, rgba(255, 255, 255, 0.08), transparent 55%);
}


</style>

<script setup>
import RoiCalculator from '@/components/RoiCalculator.vue'
import { updates } from '@/data/updates'

// Hero variant toggle: add more labels to support more versions, then add matching v-show block in template
const heroVariants = ['A', 'B']
const activeHeroVariant = ref(0)
const latestHomepageUpdates = computed(() => updates.slice(0, 2))

onMounted(() => {
  console.log("homepage mounted")
  // if(subDomain.value)
  //   console.log("subDomain:", subDomain.value)
    // navigateTo('/hair')

})

// Smooth scroll to ROI calculator
const scrollToCalculator = () => {
  const calculatorSection = document.querySelector('#roi-calculator')
  if (calculatorSection) {
    calculatorSection.scrollIntoView({ behavior: 'smooth' })
  }
}

// const ogImageOptions = {
//   title: 'AI Hairstyles for male and female - try on any style in seconds',
//   component: 'Default',
//   image: 'https://ik.imagekit.io/aicosmetic/ogimage.png?updatedAt=1691108534569'
// }
// a. Use the Composition API
// defineOgImage(ogImageOptions)

useSeoMeta({
  title: 'SupaPM - supercharged Product Management',
  ogTitle: 'SupaPM - supercharged Product Management',
  description: 'AI Product Management for your team',
  ogDescription: 'AI Product Management for your team',
  twitterCard: 'SupaPM - supercharged Product Management'
})

// just pass a jsonld object for static jsonld
useJsonld([
  {
    '@context': 'https://schema.org',
    "@type": "SoftwareApplication",
    "name": "SupaPM",
    "operatingSystem": "ANDROID, OS X, WINDOWS, IOS, LINUX, CHROME_OS, WEB",
    "applicationCategory": "WebApplication"
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "url": "https://supapm.com"
  }
]);

</script>